import React, { FC, useState, useEffect, useRef } from 'react';
import { getContactsBySenior, updateSeniorContacts, uploadContactPhoto, deletePhoto } from '../../lib/supabase';
import { API_BASE_URL } from '../../config/apiUrl';
import { syncSeniorConfig } from '../../lib/syncConfig';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

interface Contact {
  id?: string;
  tempId?: string; // For new contacts that don't have DB IDs yet
  name: string;
  phone: string;
  relationship: string;
  is_emergency: boolean;
  photo_url?: string;
  aliases?: string[];
}

interface EditContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  seniorId: string;
  seniorName: string;
  onContactsUpdated?: () => void;
}

export const EditContactsModal: FC<EditContactsModalProps> = ({
  isOpen,
  onClose,
  seniorId,
  seniorName,
  onContactsUpdated,
}) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load contacts when modal opens
  useEffect(() => {
    if (isOpen && seniorId) {
      loadContacts();
    }
  }, [isOpen, seniorId]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const loadContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getContactsBySenior(seniorId);
      setContacts(
        data.map((c) => ({
          id: c.id,
          name: c.name,
          phone: c.phone,
          relationship: c.relationship,
          is_emergency: c.is_emergency || false,
          photo_url: c.photo_url || undefined,
          aliases: c.aliases || [],
        }))
      );
    } catch (err) {
      console.error('Failed to load contacts:', err);
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = () => {
    setContacts([
      ...contacts,
      {
        tempId: `temp-${Date.now()}-${Math.random()}`, // Stable temp ID for new contacts
        name: '',
        phone: '',
        relationship: '',
        is_emergency: false,
        aliases: []
      },
    ]);
  };

  const handleRemoveContact = (contact: Contact) => {
    setContacts(contacts.filter((c) => {
      // Use stable identifier (id or tempId) to remove the correct contact
      const currentKey = c.id || c.tempId;
      const targetKey = contact.id || contact.tempId;
      return currentKey !== targetKey;
    }));
  };

  const handleContactChange = (index: number, field: keyof Contact, value: string | boolean) => {
    const updated = [...contacts];
    updated[index] = { ...updated[index], [field]: value };
    setContacts(updated);
  };

  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [photoError, setPhotoError] = useState<string>('');
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handlePhotoUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError('');

    if (!ALLOWED_TYPES.includes(file.type)) {
      setPhotoError('Please upload a JPG, PNG, WebP, or GIF image.');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setPhotoError('Photo must be under 5 MB.');
      e.target.value = '';
      return;
    }

    setUploadingIndex(index);
    try {
      // Delete old photo if replacing
      if (contacts[index].photo_url) {
        await deletePhoto(contacts[index].photo_url!).catch(() => {});
      }
      const publicUrl = await uploadContactPhoto(file);
      handleContactChange(index, 'photo_url', publicUrl);
    } catch (err) {
      console.error('Photo upload failed:', err);
      setPhotoError('Upload failed. Please try again.');
    } finally {
      setUploadingIndex(null);
      e.target.value = '';
    }
  };

  const handlePhotoDelete = async (index: number) => {
    const url = contacts[index].photo_url;
    if (!url) return;
    try {
      await deletePhoto(url).catch(() => {});
    } finally {
      handleContactChange(index, 'photo_url', '');
    }
  };

  const handleSave = async () => {
    // Validate contacts
    const validContacts = contacts.filter((c) => c.name.trim() && c.phone.trim());

    if (validContacts.length === 0) {
      setError('Please add at least one contact with name and phone');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Update contacts in Supabase
      const updatedContacts = await updateSeniorContacts(
        seniorId,
        validContacts.map((c) => ({
          id: c.id, // Include ID to identify existing contacts
          name: c.name.trim(),
          phone: c.phone.trim(),
          relationship: c.relationship.trim() || 'Contact',
          photo_url: c.photo_url || undefined,
          is_emergency: c.is_emergency,
          aliases: c.aliases || [],
        }))
      );

      // Trigger real-time sync to Android device
      // Auth + ownership now required server-side; syncSeniorConfig attaches
      // the JWT and never throws (see lib/syncConfig.ts).
      await syncSeniorConfig(seniorId);

      // Notify parent component to refresh data
      onContactsUpdated?.();

      // Close modal - fresh data will be loaded next time it opens
      onClose();
    } catch (err) {
      console.error('Failed to update contacts:', err);
      setError('Failed to save contacts. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-brand-text/50 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-none sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-full sm:max-h-[90vh] sm:m-4 m-0 flex flex-col pointer-events-auto animate-slideUp"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-brand-border shrink-0">
            <div>
              <h2 className="font-display text-xl sm:text-2xl font-bold text-brand-text">
                Edit Contacts
              </h2>
              <p className="text-sm text-brand-text-muted mt-1">
                Managing contacts for <span className="font-semibold text-brand-text">{seniorName}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-brand-bg-warm transition-colors"
              aria-label="Close"
            >
              <span className="material-symbols-outlined text-2xl text-brand-text-muted">close</span>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <span className="material-symbols-outlined text-4xl text-brand-primary animate-spin">
                  progress_activity
                </span>
              </div>
            ) : (
              <div className="space-y-4">
                {contacts.length === 0 ? (
                  <div className="text-center py-8 text-brand-text-muted">
                    <span className="material-symbols-outlined text-5xl mb-2">contacts</span>
                    <p>No contacts yet. Add the first contact below.</p>
                  </div>
                ) : (
                  contacts.map((contact, index) => (
                    <div
                      key={contact.id || contact.tempId || `contact-${index}`}
                      className="p-4 rounded-xl border border-brand-border bg-brand-bg-warm/30"
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <span className="text-sm font-semibold text-brand-text-muted">
                          Contact {index + 1}
                        </span>
                        <button
                          onClick={() => handleRemoveContact(contact)}
                          className="p-1 rounded hover:bg-red-100 text-red-500 transition-colors"
                          title="Remove contact"
                        >
                          <span className="material-symbols-outlined text-lg">delete</span>
                        </button>
                      </div>

                      {/* Photo Upload */}
                      <div className="flex items-center gap-3 mb-3">
                        <div
                          onClick={() => uploadingIndex !== index && fileInputRefs.current[index]?.click()}
                          className={`w-14 h-14 rounded-full bg-white border border-brand-border flex items-center justify-center overflow-hidden shrink-0 transition-colors ${
                            uploadingIndex === index ? 'cursor-wait opacity-70' : 'cursor-pointer hover:border-brand-primary'
                          }`}
                        >
                          {uploadingIndex === index ? (
                            <svg className="animate-spin h-5 w-5 text-brand-primary" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                          ) : contact.photo_url ? (
                            <img src={contact.photo_url} alt={contact.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="material-symbols-outlined text-xl text-brand-text-muted">add_a_photo</span>
                          )}
                        </div>
                        <input
                          ref={(el) => { fileInputRefs.current[index] = el; }}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={(e) => handlePhotoUpload(index, e)}
                          className="hidden"
                        />
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => fileInputRefs.current[index]?.click()}
                            disabled={uploadingIndex === index}
                            className="text-xs text-brand-primary hover:underline disabled:opacity-50 text-left"
                          >
                            {contact.photo_url ? 'Change photo' : 'Add photo'}
                          </button>
                          {contact.photo_url && (
                            <button
                              type="button"
                              onClick={() => handlePhotoDelete(index)}
                              className="text-xs text-red-500 hover:underline text-left"
                            >
                              Remove photo
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-brand-text-muted mb-1">
                            Name *
                          </label>
                          <input
                            type="text"
                            value={contact.name}
                            onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                            placeholder="e.g., John Smith"
                            className="w-full px-3 py-2 rounded-lg border border-brand-border focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-brand-text-muted mb-1">
                            Phone *
                          </label>
                          <input
                            type="tel"
                            value={contact.phone}
                            onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                            placeholder="e.g., 555-123-4567"
                            className="w-full px-3 py-2 rounded-lg border border-brand-border focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-brand-text-muted mb-1">
                            Relationship
                          </label>
                          <select
                            value={contact.relationship}
                            onChange={(e) => handleContactChange(index, 'relationship', e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-brand-border focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all text-sm bg-white"
                          >
                            <option value="">Select...</option>
                            <option value="Son">Son</option>
                            <option value="Daughter">Daughter</option>
                            <option value="Spouse">Spouse</option>
                            <option value="Sibling">Sibling</option>
                            <option value="Friend">Friend</option>
                            <option value="Caregiver">Caregiver</option>
                            <option value="Doctor">Doctor</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>

                        <div className="flex items-center">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={contact.is_emergency}
                              onChange={(e) =>
                                handleContactChange(index, 'is_emergency', e.target.checked)
                              }
                              className="w-4 h-4 rounded border-brand-border text-brand-primary focus:ring-brand-primary/20"
                            />
                            <span className="text-sm text-brand-text">Emergency contact</span>
                          </label>
                        </div>

                        <div className="sm:col-span-2">
                          <label className="block text-xs font-semibold text-brand-text-muted mb-1">
                            Nicknames
                          </label>
                          <input
                            type="text"
                            value={(contact.aliases || []).join(', ')}
                            onChange={(e) => {
                              const aliases = e.target.value.split(',').map(a => a.trim()).filter(a => a.length > 0);
                              const updated = [...contacts];
                              updated[index] = { ...updated[index], aliases };
                              setContacts(updated);
                            }}
                            placeholder="e.g., Tom, Tommy, Papa"
                            className="w-full px-3 py-2 rounded-lg border border-brand-border focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}

                {/* Add Contact Button */}
                <button
                  onClick={handleAddContact}
                  className="w-full py-3 px-4 border-2 border-dashed border-brand-border rounded-xl text-brand-text-muted hover:border-brand-primary hover:text-brand-primary transition-colors flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined">add</span>
                  Add Contact
                </button>
              </div>
            )}
          </div>

          {/* Photo Error */}
          {photoError && (
            <div className="px-4 sm:px-6 py-2 bg-amber-50 border-t border-amber-100">
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <span className="material-symbols-outlined text-lg">warning</span>
                {photoError}
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="px-4 sm:px-6 py-3 bg-red-50 border-t border-red-100">
              <p className="text-sm text-red-600 flex items-center gap-2">
                <span className="material-symbols-outlined text-lg">error</span>
                {error}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-brand-border shrink-0 bg-brand-bg-warm/50">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 sm:px-6 py-2.5 text-sm font-semibold text-brand-text-light hover:text-brand-text hover:bg-brand-bg-warm rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading || uploadingIndex !== null}
              className="flex items-center gap-2 px-4 sm:px-6 py-2.5 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {saving ? (
                <>
                  <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                  Saving...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-lg">save</span>
                  Save Contacts
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditContactsModal;
